import React, { forwardRef } from 'react';
import { Button } from './button';
import { Textarea } from './textarea';
import { ChatFunctionButtons } from './chat-function-buttons';
import { MESSAGE_LIMITS } from '../../utils/chatConstants';

interface QuickAction {
  id: string;
  label: string;
  icon: string;
  action: string;
}

interface ChatInputProps {
  message: string;
  isTyping: boolean;
  showQuickActions: boolean;
  showImageOptions: boolean;
  onMessageChange: (value: string) => void;
  onSendMessage: () => void;
  onVoiceRecord: () => void;
  onImageUpload: () => void;
  onQuickAction: (action: any) => void;
  onToggleQuickActions: () => void;
  onToggleImageOptions: () => void;
  onCloseMenus: () => void;
  quickActions: readonly QuickAction[];
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(({
  message,
  isTyping,
  showQuickActions,
  showImageOptions,
  onMessageChange,
  onSendMessage,
  onVoiceRecord,
  onImageUpload,
  onQuickAction,
  onToggleQuickActions,
  onToggleImageOptions,
  onCloseMenus,
  quickActions
}, ref) => {
  return (
    <div className={`gap-2 bg-background border border-border rounded-2xl py-3 shadow-sm transition-all ${
      isTyping 
        ? 'opacity-50 pointer-events-none' 
        : 'focus-within:border-primary focus-within:border-2 focus-within:shadow-md'
    }`}>
      <div className="flex-1 relative pl-4 pr-2">
         <Textarea
           ref={ref}
           value={message}
           onChange={(e) => {
             if (isTyping) return; // Prevent input when typing
             const newValue = e.target.value;
             if (newValue.length <= MESSAGE_LIMITS.MAX_LENGTH) {
               onMessageChange(newValue);
             }
           }}
           placeholder="Đặt câu hỏi cho Ngọc..."
           className="w-full h-10 max-h-32 resize-none pr-12 border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 p-0 text-base bg-transparent"
           onKeyDown={(e) => {
             if (isTyping) return; // Prevent key events when typing
             if (e.key === 'Enter' && !e.shiftKey) {
               e.preventDefault();
               if (message.trim()) {
                 onSendMessage();
               }
             }
           }}
         />
        {/* Character Count */}
        {message.length > MESSAGE_LIMITS.WARNING_LENGTH && (
          <div className="text-xs text-muted-foreground text-right">
            {message.length}/{MESSAGE_LIMITS.MAX_LENGTH}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-4">
        {/* Function Buttons Row - Inside Input Container */}
        <ChatFunctionButtons
          isTyping={isTyping}
          showQuickActions={showQuickActions}
          showImageOptions={showImageOptions}
          onVoiceRecord={onVoiceRecord}
          onImageUpload={onImageUpload}
          onQuickAction={onQuickAction}
          onToggleQuickActions={onToggleQuickActions}
          onToggleImageOptions={onToggleImageOptions}
          onCloseMenus={onCloseMenus}
          quickActions={quickActions}
        />
         {/* Send Button - Inside Input */}
         <Button
           onClick={() => {
             if (isTyping) return; // Prevent click when typing
             onSendMessage();
           }}
           disabled={!message.trim() || isTyping}
           className="
               h-10 w-10 shrink-0 
               rounded-full 
               bg-primary 
               hover:bg-primary/90 
               disabled:opacity-50"
           size="icon"
         >
           <span className="text-base font-bold">↑</span>
         </Button>
      </div>
    </div>
  );
});
